/**
 * 🧪 Test Firebase Authentication with Backend
 *
 * This utility helps diagnose authentication issues between
 * frontend and backend Firebase token validation.
 */

import { auth } from "@/firebase";
import { logInfo, logError, logSuccess } from "@/utils/secureLogger";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";

export async function testBackendAuth() {
  try {
    logInfo("🧪 Testing backend authentication...");

    // Step 1: Check if user is logged in
    const user = auth.currentUser;
    if (!user) {
      logError("User not logged in");
      return {
        success: false,
        error: "User not logged in",
      };
    }

    logInfo("✅ User logged in", {
      uid: user.uid,
      email: user.email,
    });

    // Step 2: Get token WITHOUT refresh
    logInfo("📝 Getting token without refresh...");
    const tokenNoRefresh = await user.getIdToken(false);
    logInfo("✅ Token retrieved (no refresh)", {
      length: tokenNoRefresh.length,
      first20: tokenNoRefresh.substring(0, 20),
    });

    // Step 3: Test auth endpoint with non-refreshed token
    logInfo("🔬 Testing auth endpoint with non-refreshed token...");
    try {
      const response1 = await fetch(`${API_BASE_URL}/ai/test-auth/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenNoRefresh}`,
        },
      });

      const result1 = await response1.json();

      if (response1.ok) {
        logSuccess("✅ Auth test passed with non-refreshed token", result1);
      } else {
        logError("❌ Auth test failed with non-refreshed token", {
          status: response1.status,
          result: result1,
        });
      }
    } catch (error: any) {
      logError("❌ Auth test request failed with non-refreshed token", error);
    }

    // Step 4: Get token WITH refresh
    logInfo("🔄 Getting token with refresh...");
    const tokenWithRefresh = await user.getIdToken(true);
    logInfo("✅ Token retrieved (with refresh)", {
      length: tokenWithRefresh.length,
      first20: tokenWithRefresh.substring(0, 20),
      sameAsNoRefresh: tokenNoRefresh === tokenWithRefresh,
    });

    // Step 5: Test auth endpoint with refreshed token
    logInfo("🔬 Testing auth endpoint with refreshed token...");
    try {
      const response2 = await fetch(`${API_BASE_URL}/ai/test-auth/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenWithRefresh}`,
        },
      });

      const result2 = await response2.json();

      if (response2.ok) {
        logSuccess("✅ Auth test passed with refreshed token", result2);
      } else {
        logError("❌ Auth test failed with refreshed token", {
          status: response2.status,
          result: result2,
        });
      }
    } catch (error: any) {
      logError("❌ Auth test request failed with refreshed token", error);
    }

    // Step 6: Decode token to inspect claims
    logInfo("🔍 Decoding token (base64)...");
    try {
      const tokenParts = tokenWithRefresh.split(".");
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        logInfo("Token payload", {
          iss: payload.iss,
          aud: payload.aud,
          exp: payload.exp,
          iat: payload.iat,
          expiresAt: new Date(payload.exp * 1000).toLocaleString(),
          issuedAt: new Date(payload.iat * 1000).toLocaleString(),
          isExpired: Date.now() / 1000 > payload.exp,
        });
      }
    } catch (error: any) {
      logError("Failed to decode token", error);
    }

    logSuccess("🧪 Authentication test complete");

    return {
      success: true,
      message: "Test complete - check console for details",
    };
  } catch (error: any) {
    logError("🧪 Authentication test failed", error);
    return {
      success: false,
      error: error.message,
    };
  }
}

// Expose to window for easy browser console testing
if (typeof window !== "undefined") {
  (window as any).testBackendAuth = testBackendAuth;
}
